import { Tractor, Truck, type LucideIcon } from 'lucide-react';

import type { EtiquetaDaOpcao, OpcaoDeSelecao } from '@/componentes/Campo';

/**
 * A MÁQUINA NUMA LISTA DE ESCOLHA, igual em todo o sistema (pedido do dono,
 * 31/10/2026: "otimizar as informações, reduzir o tamanho do tipo e colocar
 * como tags o código interno e a placa, usar cores nessas tags. Esse tipo de
 * cuidado evita erros e deve ser aplicado em todo o sistema").
 *
 * - RÓTULO: o código da máquina, e só ele ("BAS-001"). Curto, nunca cortado.
 * - ETIQUETAS: o código interno ("CB-07", tom de informação) e a placa
 *   ("ABC1D23", tom escuro), cada uma quando houver. São os dois números que
 *   a pessoa confere de olho no campo, e por isso ficam ao lado do código.
 * - DETALHE: a segunda linha, em 12 px: o tipo (ou a descrição, quando não há
 *   tipo), o modelo e a obra. Nada do que já virou etiqueta se repete nela.
 *
 * Função pura: a mesa (`CampoSelecao`) e o celular (`Selecao`) montam a opção
 * por aqui, a partir do que a rota de cada tela entrega.
 */

/**
 * `truck` para o que anda na estrada (odômetro, caminhão, veículo) e
 * `tractor` para a máquina pesada — os dois do vocabulário (p.19). A mesma
 * regra do celular (`ehCaminhao`, em `src/mobile/frota.ts`).
 */
export function iconeDoTipo(tipo: string | null | undefined, tipoMedidor?: string | null): LucideIcon {
  if (tipoMedidor === 'odometro') return Truck;
  return /caminh|ve[ií]culo|carro|camionete|pick/i.test(tipo ?? '') ? Truck : Tractor;
}

/** O que a função lê da máquina: só o código é obrigatório; o resto entra quando a rota traz. */
export interface MaquinaDaEscolha {
  readonly id: string;
  readonly codigo: string;
  readonly descricao?: string | null;
  readonly tipo?: string | null;
  readonly modelo?: string | null;
  readonly tipoMedidor?: string | null;
  /** A obra corrente. `null` é "sem obra"; ausente é "a rota não diz", e aí a obra não entra. */
  readonly obra?: string | null;
  readonly codigoInterno?: string | null;
  readonly placa?: string | null;
  readonly foto?: { readonly miniaturaUrl?: string | null } | null;
}

export interface ComoMostrarAMaquina {
  /** O valor da opção. Padrão: o id; o lançamento do combustível usa o código. */
  readonly valor?: string;
  /**
   * A obra da segunda linha, quando a tela a sabe por outro caminho. `false`
   * tira a obra (a tela já está dentro de uma obra, e repeti-la é ruído).
   */
  readonly obra?: string | null | false;
  /** Sem ícone do tipo (a lista do celular, que é texto). */
  readonly semIcone?: boolean;
}

function limpo(texto: string | null | undefined): string | null {
  const t = texto?.trim() ?? '';
  return t === '' ? null : t;
}

function contem(dentro: string, parte: string): boolean {
  return dentro.toLocaleLowerCase('pt-BR').includes(parte.toLocaleLowerCase('pt-BR'));
}

/** O código interno e a placa, nessa ordem, cada um quando houver e quando não for o próprio código. */
export function etiquetasDaMaquina(m: Pick<MaquinaDaEscolha, 'codigo' | 'codigoInterno' | 'placa'>): readonly EtiquetaDaOpcao[] {
  const interno = limpo(m.codigoInterno);
  const placa = limpo(m.placa);
  const codigo = m.codigo.trim().toLocaleUpperCase('pt-BR');
  return [
    ...(interno && interno.toLocaleUpperCase('pt-BR') !== codigo ? [{ texto: interno, tom: 'informacao' as const, titulo: 'Código interno' }] : []),
    ...(placa && placa.toLocaleUpperCase('pt-BR') !== codigo ? [{ texto: placa, tom: 'escuro' as const, titulo: 'Placa' }] : []),
  ];
}

/**
 * A segunda linha: o tipo (ou, sem tipo, a descrição), o modelo quando o tipo
 * ainda não o diz, e a obra. Sem nada disso, `undefined` (a opção fica numa
 * linha só).
 */
export function detalheDaMaquina(m: MaquinaDaEscolha, como: ComoMostrarAMaquina = {}): string | undefined {
  const codigo = m.codigo.trim();
  const descricao = limpo(m.descricao);
  const oQueE = limpo(m.tipo) ?? (descricao && descricao !== codigo ? descricao : null);
  const modelo = limpo(m.modelo);
  const partes: string[] = [];
  if (oQueE) partes.push(oQueE);
  if (modelo && !(oQueE && contem(oQueE, modelo))) partes.push(modelo);
  if (como.obra !== false) {
    const obra = como.obra !== undefined ? como.obra : m.obra;
    if (obra !== undefined) partes.push(limpo(obra) ?? 'sem obra');
  }
  return partes.length > 0 ? partes.join(' · ') : undefined;
}

/** A máquina como opção de escolha: o código no rótulo, o código interno e a placa em etiqueta, o resto embaixo. */
export function opcaoDaMaquina(m: MaquinaDaEscolha, como: ComoMostrarAMaquina = {}): OpcaoDeSelecao {
  const detalhe = detalheDaMaquina(m, como);
  const etiquetas = etiquetasDaMaquina(m);
  const url = limpo(m.foto?.miniaturaUrl);
  return {
    valor: como.valor ?? m.id,
    rotulo: m.codigo,
    ...(detalhe ? { detalhe } : {}),
    ...(etiquetas.length > 0 ? { etiquetas } : {}),
    ...(como.semIcone ? {} : { icone: iconeDoTipo(m.tipo ?? m.descricao, m.tipoMedidor) }),
    ...(url ? { imagem: url } : {}),
    /* O que o adesivo da máquina traz (decisão 64): o campo com leitor acha a
       opção por aqui, seja qual for o `valor` dela. */
    adesivo: { id: m.id, codigo: m.codigo },
  };
}
